import React, { useEffect, useRef, useState } from "react";
import { Scene } from "../styles/3D/styles";
import { rgbaToHex, getScale } from "../3D/utils";
import { setFontUrl } from "../data/fontData";
import THREEJS from "./three";
import * as CANNON from "cannon-es";
import animations from "../animations/animations";
import { BREAKPOINTS } from "../styles/variables/breakpoints";
import { getDeviceInfo } from "../data/utils";

const ThreeJsScene = ({ footerData, articleData }) => {
  const [hasMounted, setHasMounted] = useState(false);
  const mountRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const controlsRef = useRef(null);
  const scaleRef = useRef(getScale());
  const ctaFontRef = useRef(null);
  const fontRef = useRef(null);
  const homeFontRef = useRef(null);

  const boxWidthRef = useRef(4);
  const boxHeightRef = useRef(6);

  const animationStarted = useRef(false);
  const activateScene = useRef(false);

  const isDraggingRef = useRef(false);
  const jointConstraintRef = useRef(null);
  const lastTimeRef = useRef(null);

  const selectedBodyRef = useRef(null);
  const selectedMeshRef = useRef(null);

  const meshesRef = useRef([]);
  const bodiesRef = useRef([]);

  const timeStep = 1 / 60;

  const GROUP_DEFAULT = 1;
  const GROUP_TRIANGLE = 2;

  const colors = {
    primary: rgbaToHex(articleData?.colors.primary || "rgba(255, 255, 255, 1)"),
    secondary: rgbaToHex(
      articleData?.colors.secondary || "rgba(255, 255, 255, 1)"
    ),
    tertiary: rgbaToHex(
      articleData?.colors.tertiary || "rgba(255, 255, 255, 1)"
    ),
    nextArticlePrimary: rgbaToHex(
      articleData?.colors.next_article_primary || "rgba(255, 255, 255, 1)"
    ),
    nextArticleSecondary: rgbaToHex(
      articleData?.colors.next_article_secondary || "rgba(255, 255, 255, 1)"
    ),
  };

  const { heading, cta } = footerData;
  const { showCTA, showNextArticle } = articleData;

  const articleId = parseFloat(articleData?.id);
  const totalArticles = 3;

  const footerArray = Array.isArray(cta) ? cta : Object.values(cta);
  const ctaLink = footerArray.find(link => link?.url)?.url || null;
  const ctaHeading =
    footerArray.find(link => link?.buttonText)?.buttonText || null;

  const { currentFontUrl, nextArticleFontUrl, ctaUrl } = setFontUrl({
    articleId,
    totalArticles,
  });

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !hasMounted) return;

    const { isMobile } = getDeviceInfo();
    const appWrapper = document.querySelector(".app-wrapper");

    if (!appWrapper) return;

    const width = appWrapper.clientWidth;
    const height = appWrapper.clientHeight;

    const currentMount = mountRef.current;

    const aspect = width / height;
    const zoom = 2.5;

    cameraRef.current = new THREEJS.OrthographicCamera(
      -zoom * aspect,
      zoom * aspect,
      zoom,
      -zoom,
      0.1,
      1000
    );
    cameraRef.current.position.set(0, 0, 10);
    cameraRef.current.lookAt(0, 0, 0);

    // Scene
    sceneRef.current = new THREEJS.Scene();
    sceneRef.current.background = new THREEJS.Color(0xffffff);

    // Renderer
    rendererRef.current = new THREEJS.WebGLRenderer({ antialias: true });
    rendererRef.current.setSize(width, height);

    const canvas = rendererRef.current.domElement;

    currentMount.appendChild(canvas);

    // Controls
    controlsRef.current = new THREEJS.OrbitControls(cameraRef.current, canvas);

    controlsRef.current.enabled = false;

    // Lights
    const ambientLight = new THREEJS.AmbientLight(0xffffff);
    sceneRef.current.add(ambientLight);

    // Raycaster for mouse interactions
    const raycaster = new THREEJS.Raycaster();

    // Floor
    const floorGeometry = new THREEJS.PlaneGeometry(100, 100);
    floorGeometry.rotateX(-Math.PI / 2);
    const floorMaterial = new THREEJS.MeshBasicMaterial({
      color: 0xff0000,
    });
    floorMaterial.transparent = true;
    const floor = new THREEJS.Mesh(floorGeometry, floorMaterial);
    floor.position.y = -2;
    sceneRef.current.add(floor);

    // Movement plane when dragging
    const planeGeometry = new THREEJS.PlaneGeometry(1000, 1000);
    const movementPlane = new THREEJS.Mesh(
      planeGeometry,
      floorMaterial.clone()
    );
    movementPlane.visible = false;
    sceneRef.current.add(movementPlane);

    // Preloaded fonts

    const fontLoader = new THREEJS.FontLoader();

    fontLoader.load(
      ctaUrl,
      font => {
        ctaFontRef.current = font;
      },
      undefined,
      err => console.error("CTA font load error", err)
    );

    fontLoader.load(
      nextArticleFontUrl,
      font => {
        fontRef.current = font;
      },
      undefined,
      err => console.error("Font load error", err)
    );

    if (!showNextArticle) {
      fontLoader.load(
        currentFontUrl,
        font => {
          homeFontRef.current = font;
        },
        undefined,
        err => console.error("Home font load error", err)
      );
    }

    // ****Physics - Cannon.js****

    const world = new CANNON.World();
    world.gravity.set(0, -10, 0);

    const cubeMaterial = new CANNON.Material("cubeMaterial");

    const contactMaterial = new CANNON.ContactMaterial(
      cubeMaterial,
      cubeMaterial,
      {
        friction: 0.5, // Increase friction for more realistic sliding
        restitution: 0.25, // Lower bounce
      }
    );

    world.addContactMaterial(contactMaterial);

    const addRoundedSquare = position => {
      const scale = scaleRef.current;
      const sizeX = 0.9 * scale;
      const sizeY = 1.2 * scale;
      const radius = 0.2 * scale;

      const shape = new THREEJS.Shape();
      shape.moveTo(-sizeX / 2 + radius, -sizeY / 2);
      shape.lineTo(sizeX / 2 - radius, -sizeY / 2);
      shape.quadraticCurveTo(
        sizeX / 2,
        -sizeY / 2,
        sizeX / 2,
        -sizeY / 2 + radius
      );
      shape.lineTo(sizeX / 2, sizeY / 2 - radius);
      shape.quadraticCurveTo(
        sizeX / 2,
        sizeY / 2,
        sizeX / 2 - radius,
        sizeY / 2
      );
      shape.lineTo(-sizeX / 2 + radius, sizeY / 2);
      shape.quadraticCurveTo(
        -sizeX / 2,
        sizeY / 2,
        -sizeX / 2,
        sizeY / 2 - radius
      );
      shape.lineTo(-sizeX / 2, -sizeY / 2 + radius);
      shape.quadraticCurveTo(
        -sizeX / 2,
        -sizeY / 2,
        -sizeX / 2 + radius,
        -sizeY / 2
      );

      const geometry = new THREEJS.ShapeGeometry(shape);
      const material = new THREEJS.MeshBasicMaterial({ color: colors.primary });
      const mesh = new THREEJS.Mesh(geometry, material);
      mesh.position.set(position.x, position.y, 0);
      sceneRef.current.add(mesh);
      meshesRef.current.push(mesh);

      const shapeBody = new CANNON.Body({
        mass: 5,
        shape: new CANNON.Box(new CANNON.Vec3(sizeX / 2, sizeY / 2, 0.1)),
        position,
        angularDamping: 0.9,
        linearDamping: 0.9,
      });
      shapeBody.angularFactor.set(0, 0, 1);
      shapeBody.linearFactor.set(1, 1, 0);

      shapeBody.allowSleep = true;
      shapeBody.sleepSpeedLimit = 1.0;
      shapeBody.sleepTimeLimit = 1;

      shapeBody.collisionFilterGroup = GROUP_DEFAULT;
      shapeBody.collisionFilterMask = GROUP_DEFAULT | GROUP_TRIANGLE;

      world.addBody(shapeBody);
      bodiesRef.current.push(shapeBody);
    };

    const addRoundedRect = position => {
      const scale = scaleRef.current;
      const width = 1.6 * scale;
      const height = 0.6 * scale;
      const radius = 0.15 * scale;

      const shape = new THREEJS.Shape();
      shape.moveTo(-width / 2 + radius, -height / 2);
      shape.lineTo(width / 2 - radius, -height / 2);
      shape.quadraticCurveTo(
        width / 2,
        -height / 2,
        width / 2,
        -height / 2 + radius
      );
      shape.lineTo(width / 2, height / 2 - radius);
      shape.quadraticCurveTo(
        width / 2,
        height / 2,
        width / 2 - radius,
        height / 2
      );
      shape.lineTo(-width / 2 + radius, height / 2);
      shape.quadraticCurveTo(
        -width / 2,
        height / 2,
        -width / 2,
        height / 2 - radius
      );
      shape.lineTo(-width / 2, -height / 2 + radius);
      shape.quadraticCurveTo(
        -width / 2,
        -height / 2,
        -width / 2 + radius,
        -height / 2
      );

      const geometry = new THREEJS.ShapeGeometry(shape, 32);
      const material = new THREEJS.MeshBasicMaterial({
        color: colors.secondary,
      });
      const mesh = new THREEJS.Mesh(geometry, material);
      mesh.position.set(position.x, position.y, 0);
      sceneRef.current.add(mesh);
      meshesRef.current.push(mesh);

      // ✅ Here's your physics part
      const shapeBody = new CANNON.Body({
        mass: 4,
        shape: new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, 0.1)),
        position,
        angularDamping: 0.9,
        linearDamping: 0.9,
      });
      shapeBody.angularFactor.set(0, 0, 0);
      shapeBody.linearFactor.set(1, 1, 0);

      shapeBody.allowSleep = true;
      shapeBody.sleepSpeedLimit = 1.0;
      shapeBody.sleepTimeLimit = 1;

      shapeBody.collisionFilterGroup = GROUP_DEFAULT;
      shapeBody.collisionFilterMask = GROUP_DEFAULT | GROUP_TRIANGLE;

      world.addBody(shapeBody);
      bodiesRef.current.push(shapeBody);
    };

    const addRoundedTriangle = position => {
      const scale = scaleRef.current;

      const shape = new THREEJS.Shape();

      const s = 0.4 * scale;

      const p1 = new THREEJS.Vector2(-1.5 * s, -1.3 * s);
      const p2 = new THREEJS.Vector2(1.8 * s, -1.6 * s);
      const p3 = new THREEJS.Vector2(-0.39 * s, 1.1 * s);
      const r = 0.4 * s;

      shape.moveTo(p1.x + r, p1.y);
      shape.lineTo(p2.x - r, p2.y);
      shape.quadraticCurveTo(p2.x, p2.y, p2.x - r * 0.3, p2.y + r * 1.2);
      shape.lineTo(p3.x + r * 0.8, p3.y - r * 0.8);
      shape.quadraticCurveTo(p3.x, p3.y, p3.x - r * 0.8, p3.y - r * 0.8);
      shape.lineTo(p1.x + r * 0.2, p1.y + r * 1.2);
      shape.quadraticCurveTo(p1.x, p1.y, p1.x + r, p1.y);
      shape.closePath();

      const geometry = new THREEJS.ShapeGeometry(shape, 64);
      const material = new THREEJS.MeshBasicMaterial({
        color: colors.tertiary,
      });
      const mesh = new THREEJS.Mesh(geometry, material);
      mesh.position.set(position.x, position.y, 0);
      sceneRef.current.add(mesh);
      meshesRef.current.push(mesh);

      const thickness = 0.01;
      const halfZ = thickness / 2;

      const front = [p1, p2, p3].map(p => new CANNON.Vec3(p.x, p.y, halfZ));
      const back = [p1, p2, p3].map(p => new CANNON.Vec3(p.x, p.y, -halfZ));
      const vertices = [...front, ...back];

      const faces = [
        [0, 1, 2],
        [5, 4, 3],

        [0, 3, 4],
        [0, 4, 1],

        [1, 4, 5],
        [1, 5, 2],

        [2, 5, 3],
        [2, 3, 0],
      ];

      const body = new CANNON.Body({
        mass: 4,
        shape: new CANNON.ConvexPolyhedron({ vertices, faces }),
        position: new CANNON.Vec3(position.x, position.y, 0),
        angularDamping: 0.9,
        linearDamping: 0.9,
      });

      body.angularFactor.set(0, 0, 1);
      body.linearFactor.set(1, 1, 0);
      body.allowSleep = true;
      body.sleepSpeedLimit = 1.0;
      body.sleepTimeLimit = 1;

      // ✅ Proper collision group + mask
      body.collisionFilterGroup = GROUP_TRIANGLE;
      body.collisionFilterMask = GROUP_DEFAULT | GROUP_TRIANGLE;

      world.addBody(body);
      bodiesRef.current.push(body);
    };

    const addTextShape = (text, position) => {
      if (!fontRef.current) {
        console.warn("Font not loaded yet");
        return;
      }

      const scale = scaleRef.current;
      const font = showNextArticle ? fontRef.current : homeFontRef.current;
      const size = 1.2 * scale;
      const fontColor = showNextArticle
        ? colors.nextArticlePrimary
        : colors.primary;

      const geometry = new THREEJS.TextGeometry(text, {
        font,
        size,
        height: 0.05,
        curveSegments: 16,
        bevelEnabled: false,
      });

      geometry.computeBoundingBox();

      const centerOffset = geometry.boundingBox
        .getCenter(new THREEJS.Vector3())
        .negate();

      geometry.translate(centerOffset.x, centerOffset.y, 0);

      const material = new THREEJS.MeshBasicMaterial({
        color: fontColor,
      });

      const mesh = new THREEJS.Mesh(geometry, material);
      mesh.position.set(position.x, position.y, 0);
      sceneRef.current.add(mesh);
      meshesRef.current.push(mesh);

      const sizeVec = new THREEJS.Vector3();
      geometry.boundingBox.getSize(sizeVec);

      const body = new CANNON.Body({
        mass: 4,
        shape: new CANNON.Box(
          new CANNON.Vec3(sizeVec.x / 2, sizeVec.y / 2, 0.05)
        ),
        position: new CANNON.Vec3(
          position.x + centerOffset.x,
          position.y + centerOffset.y,
          0
        ),
        angularDamping: 0.9,
        linearDamping: 0.9,
      });

      body.angularFactor.set(0, 0, 1);
      body.linearFactor.set(1, 1, 0);
      body.allowSleep = true;
      body.sleepSpeedLimit = 1.0;
      body.sleepTimeLimit = 1;

      world.addBody(body);
      bodiesRef.current.push(body);
    };

    const addCTAButton = (text, position, url) => {
      if (!ctaFontRef.current) {
        console.warn("CTA font not loaded yet");
        return;
      }

      const scale = scaleRef.current;
      const width = 1.6 * scale;
      const height = 0.6 * scale;
      const radius = 0.15 * scale;

      const materialColor = showNextArticle
        ? colors.nextArticleSecondary
        : colors.secondary;
      const textColor = showNextArticle
        ? colors.nextArticlePrimary
        : colors.primary;

      // Create rounded rectangle shape
      const shape = new THREEJS.Shape();
      shape.moveTo(-width / 2 + radius, -height / 2);
      shape.lineTo(width / 2 - radius, -height / 2);
      shape.quadraticCurveTo(
        width / 2,
        -height / 2,
        width / 2,
        -height / 2 + radius
      );
      shape.lineTo(width / 2, height / 2 - radius);
      shape.quadraticCurveTo(
        width / 2,
        height / 2,
        width / 2 - radius,
        height / 2
      );
      shape.lineTo(-width / 2 + radius, height / 2);
      shape.quadraticCurveTo(
        -width / 2,
        height / 2,
        -width / 2,
        height / 2 - radius
      );
      shape.lineTo(-width / 2, -height / 2 + radius);
      shape.quadraticCurveTo(
        -width / 2,
        -height / 2,
        -width / 2 + radius,
        -height / 2
      );

      const geometry = new THREEJS.ShapeGeometry(shape);
      const material = new THREEJS.MeshBasicMaterial({
        color: materialColor,
      });

      const mesh = new THREEJS.Mesh(geometry, material);

      mesh.position.set(position.x, position.y, 0);
      mesh.userData.isCTA = true;
      mesh.userData.url = url;
      sceneRef.current.add(mesh);
      meshesRef.current.push(mesh);

      const shapeBody = new CANNON.Body({
        mass: 4,
        shape: new CANNON.Box(new CANNON.Vec3(width / 2, height / 2, 0.1)),
        position,
        angularDamping: 0.9,
        linearDamping: 0.9,
      });

      shapeBody.angularFactor.set(0, 0, 1);
      shapeBody.linearFactor.set(1, 1, 0);

      world.addBody(shapeBody);
      bodiesRef.current.push(shapeBody);

      const textGeometry = new THREEJS.TextGeometry(text, {
        font: ctaFontRef.current,
        size: 0.1 * scale,
        height: 0.05,
        curveSegments: 12,
      });

      textGeometry.computeBoundingBox();
      const centerOffset = textGeometry.boundingBox
        .getCenter(new THREEJS.Vector3())
        .negate();
      textGeometry.translate(centerOffset.x, centerOffset.y, 0.05);

      const textMaterial = new THREEJS.MeshBasicMaterial({
        color: textColor,
      });
      const textMesh = new THREEJS.Mesh(textGeometry, textMaterial);

      mesh.add(textMesh);
    };

    // Floor
    const floorBody = new CANNON.Body({
      mass: 0,
      shape: new CANNON.Plane(),
      position: new CANNON.Vec3(0, -2, 0),
    });
    floorBody.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
    world.addBody(floorBody);

    // Joint body
    const jointBody = new CANNON.Body({
      mass: 0,
      shape: new CANNON.Sphere(0.1),
    });

    world.addBody(jointBody);
    jointBody.collisionFilterGroup = 0;
    jointBody.collisionFilterMask = 0;
    jointBody.linearFactor.set(1, 1, 0);
    world.addBody(jointBody);

    const getHitPoint = (clientX, clientY, meshes, camera) => {
      // Get 3D Point from mouse click
      const mouse = new THREEJS.Vector2();
      mouse.x = (clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(clientY / window.innerHeight) * 2 + 1;

      // Get the picking ray from the point
      raycaster.setFromCamera(mouse, camera);

      // Determine if we're working with a single mesh or multiple meshes

      const intersects = Array.isArray(meshes)
        ? raycaster.intersectObjects(meshes, true)
        : raycaster.intersectObject(meshes);

      if (intersects.length > 0) {
        const hit = intersects[0];

        mountRef.current.classList.add("grabbing");

        if (Array.isArray(meshes)) {
          let selectedMesh = hit.object;
          while (selectedMesh && !meshesRef.current.includes(selectedMesh)) {
            selectedMesh = selectedMesh.parent;
          }

          if (selectedMesh) {
            selectedMeshRef.current = selectedMesh;
            const meshIndex = meshesRef.current.indexOf(selectedMesh);
            selectedBodyRef.current = bodiesRef.current[meshIndex];

            if (selectedBodyRef.current) {
              selectedBodyRef.current.angularVelocity.set(0, 0, 0);
              selectedBodyRef.current.velocity.set(0, 0, 0);
              selectedBodyRef.current.wakeUp();
            }
          }

          selectedBodyRef.current.angularVelocity.set(0, 0, 0);
          selectedBodyRef.current.velocity.set(0, 0, 0);
          selectedBodyRef.current.wakeUp();
        }

        return hit.point;
      }

      return null;
    };

    // Moves the virtual movement plane for the mouseJoint to move in
    const moveMovementPlane = (point, camera) => {
      movementPlane.position.copy(point);
      movementPlane.rotation.set(0, 0, 0);
    };

    // Adds a constrain between the cube and the joint body
    const addJointConstraint = (position, constrainedBody) => {
      // Vector that goes from the body to the clicked point
      const vector = new CANNON.Vec3()
        .copy(position)
        .vsub(constrainedBody.position);

      // Apply anti-quaternion to vector to transform it to the local body coordinate system
      const antiRotation = constrainedBody.quaternion.inverse();
      const pivot = antiRotation.vmult(vector); // pivot is not in local body coordinates

      // Move the cannon click marker body to the click position
      jointBody.position.copy(position);

      // Create a new constraint
      jointConstraintRef.current = new CANNON.PointToPointConstraint(
        constrainedBody,
        pivot,
        jointBody,
        new CANNON.Vec3(0, 0, 0)
      );

      // Add the constraint to world
      world.addConstraint(jointConstraintRef.current);
    };

    // This functions moves the joint body to a new position in space and updates the constraint
    const moveJoint = position => {
      const margin = 0.2 * scaleRef.current;

      const minX = -boxWidthRef.current + margin;
      const maxX = boxWidthRef.current - margin;
      const minY = -boxHeightRef.current + margin;
      const maxY = boxHeightRef.current - margin;

      position.x = Math.max(minX, Math.min(maxX, position.x));
      position.y = Math.max(minY, Math.min(maxY, position.y));
      position.z = 0;

      jointBody.position.copy(position);
      jointConstraintRef.current.update();
    };
    const removeJointConstraint = () => {
      world.removeConstraint(jointConstraintRef.current);
      jointConstraintRef.current = null;
    };

    const onPointerDown = event => {
      const hitPoint = getHitPoint(
        event.clientX,
        event.clientY,
        meshesRef.current,
        cameraRef.current
      );

      if (!hitPoint) return;

      if (
        selectedMeshRef.current?.userData.isCTA &&
        selectedMeshRef.current.userData.url
      ) {
        window.location.href = selectedMeshRef.current.userData.url;
        return;
      }

      if (isMobile) return;

      // Check if click is inside bounds
      const isInside =
        hitPoint.x > -boxWidthRef.current &&
        hitPoint.x < boxWidthRef.current &&
        hitPoint.y > -boxHeightRef.current &&
        hitPoint.y < boxHeightRef.current;

      if (!isInside) return;
      moveMovementPlane(hitPoint, cameraRef.current);
      addJointConstraint(hitPoint, selectedBodyRef.current);
      isDraggingRef.current = true;
    };

    const onPointerMove = event => {
      if (!isDraggingRef.current) return;

      const hitPoint = getHitPoint(
        event.clientX,
        event.clientY,
        movementPlane,
        cameraRef.current
      );

      if (!hitPoint) return;

      moveJoint(hitPoint);
    };

    const onPointerUp = () => {
      isDraggingRef.current = false;

      selectedBodyRef.current = null;
      selectedMeshRef.current = null;

      removeJointConstraint();

      mountRef.current.classList.remove("grabbing");
    };

    const render = () => {
      controlsRef.current.update();
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    };

    const animate = time => {
      requestAnimationFrame(animate);

      if (!activateScene.current) return;

      if (lastTimeRef.current !== undefined) {
        const delta = (time - lastTimeRef.current) / 1000;
        world.step(timeStep, delta, 3);
      }

      lastTimeRef.current = time;

      const margin = 0.2 * scaleRef.current;
      const minX = -boxWidthRef.current + margin;
      const maxX = boxWidthRef.current - margin;
      const minY = -boxHeightRef.current + margin;
      const maxY = boxHeightRef.current - margin;

      bodiesRef.current.forEach((body, index) => {
        body.position.x = Math.max(minX, Math.min(maxX, body.position.x));
        body.position.y = Math.max(minY, Math.min(maxY, body.position.y));

        if (meshesRef.current[index]) {
          meshesRef.current[index].position.copy(body.position);
          meshesRef.current[index].quaternion.copy(body.quaternion);
        }
      });

      render();
    };

    const resize = () => {
      if (!cameraRef.current || !rendererRef.current || !mountRef.current)
        return;

      const appWrapper = document.querySelector(".app-wrapper");

      if (!appWrapper) return;

      const width = appWrapper.clientWidth;
      const height = appWrapper.clientHeight;
      const aspect = width / height;

      const zoom = 2.5;

      cameraRef.current.left = -zoom * aspect;
      cameraRef.current.right = zoom * aspect;
      cameraRef.current.top = zoom;
      cameraRef.current.bottom = -zoom;

      cameraRef.current.updateProjectionMatrix();

      rendererRef.current.setSize(width, height);
      rendererRef.current.setPixelRatio(window.devicePixelRatio);

      scaleRef.current = getScale();

      boxWidthRef.current = zoom * aspect - 0.5;
      boxHeightRef.current = zoom - 0.5;
    };

    const mm = animations.matchMedia();
    let delay = 1000;

    mm.add(`(min-width: ${BREAKPOINTS.sm}px)`, () => {
      animations.ScrollTrigger.create({
        trigger: mountRef.current,
        start: "top center",
        onEnter: () => {
          if (!animationStarted.current) {
            animationStarted.current = true;

            animate();

            setTimeout(() => addRoundedTriangle(new CANNON.Vec3(-3, 6, 0)), 0);
            setTimeout(() => addRoundedSquare(new CANNON.Vec3(-5, 8, 0)), 0);
            setTimeout(() => addRoundedSquare(new CANNON.Vec3(-1, 8, 0)), 500);
            setTimeout(() => addRoundedRect(new CANNON.Vec3(-5, 8, 0)), 800);

            setTimeout(() => addRoundedRect(new CANNON.Vec3(0, 6, 0)), 0);
            setTimeout(() => addRoundedTriangle(new CANNON.Vec3(2.5, 6, 0)), 0);
            setTimeout(
              () => addRoundedTriangle(new CANNON.Vec3(10, 8, 0)),
              500
            );
            setTimeout(() => addRoundedSquare(new CANNON.Vec3(1.5, 8, 0)), 500);
            setTimeout(() => addRoundedSquare(new CANNON.Vec3(3, 8, 0)), 800);
            setTimeout(() => addRoundedRect(new CANNON.Vec3(7, 8, 0)), 800);

            Object.entries(heading).forEach(([key, value], index) => {
              const position = new CANNON.Vec3(-1 + index * 3, 6, 0);

              setTimeout(() => {
                addTextShape(value, position);
              }, delay);

              delay += 500;
            });
          }

          activateScene.current = true;
        },
        onLeaveBack: () => {
          activateScene.current = false;
        },
      });
    });

    mm.add(`(max-width: ${BREAKPOINTS.sm - 1}px)`, () => {
      animations.ScrollTrigger.create({
        trigger: mountRef.current,
        start: "top center",
        onEnter: () => {
          if (!animationStarted.current) {
            animationStarted.current = true;

            animate();

            setTimeout(() => addRoundedRect(new CANNON.Vec3(-3, 6, 0)), 0);
            setTimeout(() => addRoundedSquare(new CANNON.Vec3(15, 8, 0)), 0);
            setTimeout(
              () => addRoundedTriangle(new CANNON.Vec3(-5, 8, 0)),
              250
            );
            setTimeout(
              () => addRoundedTriangle(new CANNON.Vec3(10, 6, 0)),
              400
            );
            setTimeout(() => addRoundedSquare(new CANNON.Vec3(-3, 6, 0)), 600);
            setTimeout(() => addRoundedRect(new CANNON.Vec3(10, 6, 0)), 600);

            setTimeout(() => addRoundedRect(new CANNON.Vec3(-5, 6, 0)), 700);

            setTimeout(() => addRoundedSquare(new CANNON.Vec3(15, 6, 0)), 800);

            Object.entries(heading).forEach(([key, value], index) => {
              const position = new CANNON.Vec3(-1 + index * 2, 2.5, 0);

              setTimeout(() => {
                addTextShape(value, position);
              }, delay);

              delay += 600;
            });

            setTimeout(() => {
              if (!showCTA) return;
              addCTAButton(ctaHeading, new CANNON.Vec3(15, 9, 0), ctaLink);
            }, (delay += 500));
          }

          activateScene.current = true;
        },
        onLeaveBack: () => {
          activateScene.current = false;
        },
      });
    });
    resize();

    window.addEventListener("resize", resize);

    if (!isMobile) {
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
      window.addEventListener("pointerdown", onPointerDown);
    }

    return () => {
      window.removeEventListener("resize", resize);

      if (!isMobile) {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointerdown", onPointerDown);
      }

      meshesRef.current.forEach(mesh => {
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach(m => m.dispose());
          } else {
            mesh.material.dispose();
          }
        }
        sceneRef.current?.remove(mesh);
      });
      meshesRef.current = [];

      bodiesRef.current.forEach(body => world.removeBody(body));
      bodiesRef.current = [];

      if (jointConstraintRef.current) {
        world.removeConstraint(jointConstraintRef.current);
        jointConstraintRef.current = null;
      }

      if (rendererRef.current) {
        rendererRef.current.dispose();
        if (
          rendererRef.current &&
          currentMount.contains(rendererRef.current.domElement)
        ) {
          currentMount.removeChild(rendererRef.current.domElement);
        }
      }

      mm.revert();

      cameraRef.current = null;
      rendererRef.current = null;
      sceneRef.current = null;
      controlsRef.current = null;
    };
  }, [hasMounted]);

  return (
    <div suppressHydrationWarning={true}>
      {hasMounted ? <Scene ref={mountRef} /> : null}
    </div>
  );
};

export default ThreeJsScene;
